/** Hourly shortwave radiation (W/m²) for the current local solar day via Open-Meteo. */

const OPEN_METEO_FORECAST = 'https://api.open-meteo.com/v1/forecast';

export function getZonedHourKey(date, timeZone) {
  if (!timeZone || !(date instanceof Date) || Number.isNaN(date.getTime())) {
    return null;
  }
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      hour12: false,
    })
      .formatToParts(date)
      .filter(part => part.type !== 'literal')
      .map(part => [part.type, part.value])
  );
  if (!parts.year || !parts.month || !parts.day || parts.hour == null) {
    return null;
  }
  return `${parts.year}-${parts.month}-${parts.day}T${String(parts.hour).padStart(2, '0')}`;
}

export function getZonedDateKey(date, timeZone) {
  const hourKey = getZonedHourKey(date, timeZone);
  return hourKey ? hourKey.slice(0, 10) : null;
}

export function addDaysToDateKey(dateKey, days) {
  if (typeof dateKey !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) {
    return null;
  }
  const [year, month, day] = dateKey.split('-').map(Number);
  const next = new Date(Date.UTC(year, month - 1, day + days));
  return next.toISOString().slice(0, 10);
}

/** Normalize Open-Meteo hourly payload into one local calendar day (0–24 hours). */
export function parseInsolationHourlyForDate(data, dateKey, now = new Date()) {
  const hourly = data?.hourly;
  const timeZone = typeof data?.timezone === 'string' ? data.timezone : null;
  if (!hourly?.time?.length || !hourly?.shortwave_radiation?.length || !timeZone || !dateKey) {
    return null;
  }

  const todayKey = getZonedDateKey(now, timeZone);
  const currentHourKey = getZonedHourKey(now, timeZone);
  const isToday = dateKey === todayKey;

  const hours = [];
  for (let index = 0; index < hourly.time.length; index += 1) {
    const timeIso = hourly.time[index];
    if (typeof timeIso !== 'string' || !timeIso.startsWith(dateKey)) continue;
    const hour = Number.parseInt(timeIso.slice(11, 13), 10);
    if (!Number.isFinite(hour)) continue;
    const raw = hourly.shortwave_radiation[index];
    const radiationWm2 = raw != null && Number.isFinite(Number(raw)) ? Math.max(0, Number(raw)) : 0;
    const hourKey = timeIso.slice(0, 13);
    hours.push({
      hour,
      radiationWm2,
      isCurrent: isToday && hourKey === currentHourKey,
      isPast: isToday && hourKey < currentHourKey,
    });
  }

  if (!hours.length) return null;

  const maxRadiationWm2 = Math.max(...hours.map(entry => entry.radiationWm2), 1);
  return {
    timeZone,
    date: dateKey,
    hours: hours.map(entry => ({
      ...entry,
      levelPct: Math.round((entry.radiationWm2 / maxRadiationWm2) * 100),
    })),
    maxRadiationWm2,
  };
}

/** Normalize Open-Meteo hourly payload into today's 0–24 local hours. */
export function parseTodayInsolationHourly(data, now = new Date()) {
  const timeZone = typeof data?.timezone === 'string' ? data.timezone : null;
  const todayKey = getZonedDateKey(now, timeZone);
  if (!todayKey) return null;
  return parseInsolationHourlyForDate(data, todayKey, now);
}

export function parseTomorrowInsolationHourly(data, now = new Date()) {
  const timeZone = typeof data?.timezone === 'string' ? data.timezone : null;
  const todayKey = getZonedDateKey(now, timeZone);
  const tomorrowKey = todayKey ? addDaysToDateKey(todayKey, 1) : null;
  if (!tomorrowKey) return null;
  return parseInsolationHourlyForDate(data, tomorrowKey, now);
}

/** Same index as Open EMS: sunshine_duration / daylight_duration, 0–100. */
export function insolationPctFromSunshine(sunshineDurationSec, daylightDurationSec) {
  if (sunshineDurationSec == null || daylightDurationSec == null) {
    return null;
  }
  const sun = Number(sunshineDurationSec);
  const day = Number(daylightDurationSec);
  if (!Number.isFinite(sun) || !Number.isFinite(day) || day <= 0 || sun < 0) {
    return null;
  }
  return Math.max(0, Math.min(100, Math.round((100 * Math.min(sun, day)) / day)));
}

export function pickFiniteInsolationPct(...values) {
  for (const value of values) {
    if (value == null || value === '') continue;
    const n = Number(value);
    if (Number.isFinite(n)) {
      return Math.max(0, Math.min(100, Math.round(n)));
    }
  }
  return null;
}

/** True when local station time is before sunrise or after sunset; null if unknown. */
export function isOpenMeteoLocalNightNow(data, now = new Date()) {
  const timeZone = typeof data?.timezone === 'string' ? data.timezone : null;
  const sunrise = data?.daily?.sunrise?.[0];
  const sunset = data?.daily?.sunset?.[0];
  const nowKey = getZonedHourKey(now, timeZone);
  const sunriseHour = typeof sunrise === 'string' ? sunrise.slice(0, 13) : null;
  const sunsetHour = typeof sunset === 'string' ? sunset.slice(0, 13) : null;
  if (!nowKey || !sunriseHour || !sunsetHour) {
    return null;
  }
  return nowKey < sunriseHour || nowKey > sunsetHour;
}

export function parseDailyInsolationPercents(data, now = new Date()) {
  const sun = data?.daily?.sunshine_duration;
  const dayl = data?.daily?.daylight_duration;
  if (!Array.isArray(sun) || !Array.isArray(dayl)) {
    return { todayPct: null, tomorrowPct: null };
  }
  let todayPct = insolationPctFromSunshine(sun[0], dayl[0]);
  const tomorrowPct = insolationPctFromSunshine(sun[1], dayl[1]);
  if (todayPct != null && isOpenMeteoLocalNightNow(data, now) === true) {
    todayPct = 0;
  }
  return { todayPct, tomorrowPct };
}

const EMPTY_INSOLATION_FORECAST = {
  today: null,
  tomorrow: null,
  todayPct: null,
  tomorrowPct: null,
};

const forecastCache = new Map();
const FORECAST_CACHE_MS = 30 * 60 * 1000;

function forecastCacheKey(lat, lon) {
  return `${lat.toFixed(3)},${lon.toFixed(3)}`;
}

export async function fetchInsolationHourlyByDay(latitude, longitude) {
  const lat = Number(latitude);
  const lon = Number(longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
    return { ...EMPTY_INSOLATION_FORECAST };
  }

  const cacheKey = forecastCacheKey(lat, lon);
  const cached = forecastCache.get(cacheKey);
  if (cached && Date.now() - cached.at < FORECAST_CACHE_MS) {
    return cached.value;
  }

  const params = new URLSearchParams({
    latitude: String(lat),
    longitude: String(lon),
    hourly: 'shortwave_radiation',
    daily: 'sunshine_duration,daylight_duration,sunrise,sunset',
    forecast_days: '2',
    timezone: 'auto',
  });

  try {
    const response = await fetch(`${OPEN_METEO_FORECAST}?${params.toString()}`, { cache: 'no-store' });
    if (!response.ok) return { ...EMPTY_INSOLATION_FORECAST };
    const data = await response.json();
    const now = new Date();
    const dailyPct = parseDailyInsolationPercents(data, now);
    const value = {
      today: parseTodayInsolationHourly(data, now),
      tomorrow: parseTomorrowInsolationHourly(data, now),
      todayPct: dailyPct.todayPct,
      tomorrowPct: dailyPct.tomorrowPct,
    };
    forecastCache.set(cacheKey, { at: Date.now(), value });
    return value;
  } catch {
    return { ...EMPTY_INSOLATION_FORECAST };
  }
}

/** @deprecated Use fetchInsolationHourlyByDay */
export async function fetchTodayInsolationHourly(latitude, longitude) {
  const { today } = await fetchInsolationHourlyByDay(latitude, longitude);
  return today;
}
